/**
 * Generic resource route controller.
 *
 * Canonical resource metadata is resolved by the backend. This component owns
 * only React query adapters and route-parameter extraction.
 */
import { useParams } from 'react-router-dom';
import {
  getHubResourceByCreateRoute,
  getHubResourceByDetailRoute,
  getHubResourceByEditRoute,
  getHubResourceByListRoute,
} from '@finance/contracts';
import { getResourceHookBinding } from '../../runtime/resource-hook-registry';
import { GenericResourceListPage } from './GenericResourceListPage';
import { GenericResourceDetailPage } from './GenericResourceDetailPage';
import { GenericResourceFormPage } from './GenericResourceFormPage';
import { PlatformSupportDelegationListPage } from './PlatformSupportDelegationListPage';
import { PageNotFoundState } from '../states';

export interface ResourceRoutePageProps {
  routeId: string;
}

export function ResourceRoutePage({ routeId }: ResourceRoutePageProps) {
  const params = useParams<Record<string, string>>();
  const listResource = getHubResourceByListRoute(routeId);

  if (listResource) {
    if (listResource.id === 'platform-support-delegations') {
      return <PlatformSupportDelegationListPage />;
    }
    return <GenericResourceListPage resourceId={listResource.id} />;
  }

  const detailResource = getHubResourceByDetailRoute(routeId);
  if (detailResource) {
    const hooks = getResourceHookBinding(detailResource.id);
    const paramName = hooks?.routeParameterName ?? hooks?.paramName ?? 'id';
    const id = params[paramName] ?? '';
    if (!id) return <PageNotFoundState />;
    return (
      <GenericResourceDetailPage
        resourceId={detailResource.id}
        recordId={id}
        paramName={paramName}
      />
    );
  }

  const createResource = getHubResourceByCreateRoute(routeId);
  if (createResource) {
    return (
      <GenericResourceFormPage
        resourceId={createResource.id}
        mode="create"
      />
    );
  }

  const editResource = getHubResourceByEditRoute(routeId);
  if (editResource) {
    const hooks = getResourceHookBinding(editResource.id);
    const paramName = hooks?.routeParameterName ?? hooks?.paramName ?? 'id';
    const id = params[paramName] ?? params.id ?? '';
    if (!id) return <PageNotFoundState />;
    return (
      <GenericResourceFormPage
        resourceId={editResource.id}
        mode="edit"
        recordId={id}
      />
    );
  }

  return <PageNotFoundState />;
}